/**
 * Rechenhilfen: Bewegungskurven, Zeitachsen, Zufall mit festem Startwert und
 * Pfade. Alles ist rein und berechenbar – gleiche Eingabe, gleiches Ergebnis.
 */

/** Ein Punkt auf der Zeichenfläche. */
export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Begrenzt einen Wert auf einen Bereich. */
export function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

/** Lineare Mischung zweier Werte. */
export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/** Fortschritt von 0 bis 1 innerhalb eines Zeitabschnitts. */
export function segment(t: number, start: number, end: number): number {
  if (end <= start) {
    return t >= end ? 1 : 0;
  }
  return clamp((t - start) / (end - start));
}

/** Eine Bewegungskurve. */
export type Easing = (t: number) => number;

/** Benannte Bewegungskurven. */
export const ease = {
  linear: (t: number) => t,
  inQuad: (t: number) => t * t,
  outQuad: (t: number) => 1 - (1 - t) * (1 - t),
  inOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2),
  inCubic: (t: number) => t * t * t,
  outCubic: (t: number) => 1 - (1 - t) ** 3,
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  inOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
  /** Schießt leicht über das Ziel hinaus und federt zurück. */
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
  },
} as const satisfies Record<string, Easing>;

/**
 * Wert entlang einer Zeitachse mit Stützstellen.
 * @param frames Paare aus Zeitpunkt (0 bis 1) und Wert, aufsteigend sortiert.
 */
export function keyframes(
  t: number,
  frames: readonly (readonly [number, number])[],
  easing: Easing = ease.inOutCubic,
): number {
  const first = frames[0];
  if (first === undefined) {
    return 0;
  }
  if (t <= first[0]) {
    return first[1];
  }
  for (let index = 1; index < frames.length; index += 1) {
    const previous = frames[index - 1];
    const next = frames[index];
    if (previous !== undefined && next !== undefined && t <= next[0]) {
      return lerp(previous[1], next[1], easing(segment(t, previous[0], next[0])));
    }
  }
  return frames[frames.length - 1]?.[1] ?? first[1];
}

/** Sinusschwingung mit der angegebenen Zahl an Durchläufen je Schleife. */
export function wave(t: number, cycles = 1, phase = 0): number {
  return Math.sin((t * cycles + phase) * Math.PI * 2);
}

/** Weicher Puls von 0 auf 1 und zurück. */
export function pulse(t: number): number {
  return (1 - Math.cos(clamp(t) * Math.PI * 2)) / 2;
}

/** Bruchteil einer Zahl, immer zwischen 0 und 1. */
export function fract(value: number): number {
  return value - Math.floor(value);
}

/** Zufallszahlen mit festem Startwert (Mulberry32), Ergebnis 0 bis unter 1. */
export function random(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 0x100000000;
  };
}

/** Zahl für SVG: höchstens zwei Nachkommastellen, ohne "-0". */
export function fmt(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) || rounded === 0 ? '0' : String(rounded);
}

/** Punkt auf einem Kreis; 0 Grad ist rechts, 90 Grad unten. */
export function polar(cx: number, cy: number, r: number, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;
  return { x: cx + r * Math.cos(radians), y: cy + r * Math.sin(radians) };
}

/** Dreht einen Punkt um einen Drehpunkt. */
export function rotatePoint(point: Point, origin: Point, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return { x: origin.x + dx * cos - dy * sin, y: origin.y + dx * sin + dy * cos };
}

/**
 * Hüllkurve für Schleifen: steigt von 0 bis attack auf 1, bleibt und fällt
 * ab release bis zum Ende auf 0. Bei t = 0 und t = 1 ist sie 0, die Schleife
 * schließt also nahtlos.
 */
export function envelope(
  t: number,
  attack: number,
  release: number,
  easing: Easing = ease.inOutCubic,
): number {
  return Math.min(easing(segment(t, 0, attack)), 1 - easing(segment(t, release, 1)));
}
